import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
    Autocomplete,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Divider,
    Tooltip,
} from '@mui/material';
import { ArrowBackIosNew, Add } from '@mui/icons-material';
import * as MuiIcons from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import StyledButton from 'components/StyledButton';
import FlexCenter from 'components/FlexCenter';
import GenericDialog from 'components/GenericDialog';
import DeleteConfirm from 'components/DeleteConfirm';
import FieldDefForm, { FIELD_INITIAL } from '../fields/FieldDefForm';
import SortableFieldRow from '../SortableFieldRow';


/* ═══════════════════════════════════════════════════════════
   Constants
═══════════════════════════════════════════════════════════ */

const ICON_OPTIONS = Object.keys(MuiIcons).filter(
    name => !/(?:Outlined|Rounded|Sharp|TwoTone)$/.test(name)
);

const COLLECTION_INITIAL = {
    name: '',
    label: '',
    icon: '',
    description: '',
    isActive: true,
};

const collectionSchema = yup.object().shape({
    name:  yup.string().required('Required').matches(/^[a-z0-9_-]+$/, 'Lowercase letters, numbers, - and _ only'),
    label: yup.string().required('Required'),
});


/* ═══════════════════════════════════════════════════════════
   Main Page
═══════════════════════════════════════════════════════════ */

const CollectionDefPage = () => {
    useAuthCheck();
    const { collectionId } = useParams();
    const navigate = useNavigate();

    const isEdit = Boolean(collectionId) && collectionId !== 'new';

    const user = useSelector((state) => state.user);
    const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    const canCreate = hasRight(permissionMap, 'CollectionDefs', 'create', false);
    const canEdit   = hasRight(permissionMap, 'CollectionDefs', 'update', false);
    const canDelete = hasRight(permissionMap, 'CollectionDefs', 'delete', false);

    /* Collection state */
    const [loading, setLoading] = useState(false);
    const [collectionValues, setCollectionValues] = useState(COLLECTION_INITIAL);

    /* Fields state */
    const [fields, setFields] = useState([]);
    const [fieldSaving, setFieldSaving] = useState(false);
    const [editField, setEditField] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const sensors = useSensors(useSensor(PointerSensor));

    /* ── Load data in edit mode ─────────────────────────── */
    const fetchFields = useCallback(async () => {
        try {
            const { data } = await api.get(`/collectiondefs/${collectionId}/fields`);
            setFields(data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load fields.');
        }
    }, [collectionId]);

    useEffect(() => {
        if (!isEdit) return;
        const load = async () => {
            try {
                setLoading(true);
                const [colRes] = await Promise.all([
                    api.get(`/collectiondefs/${collectionId}`),
                ]);
                setCollectionValues({ ...COLLECTION_INITIAL, ...colRes.data });
                const fieldsRes = await api.get(`/collectiondefs/${collectionId}/fields`);
                setFields(fieldsRes.data);
            } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to load.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [collectionId, isEdit]);

    /* ── Collection submit ──────────────────────────────── */
    const handleCollectionSubmit = useCallback(async (values) => {
        try {
            const payload = { ...values, name: values.name.toLowerCase().trim() };
            if (isEdit) {
                await api.put(`/collectiondefs/${collectionId}`, payload);
                toast.success('Updated successfully.');
            } else {
                const { data } = await api.post('/collectiondefs/new', payload);
                toast.success('Created successfully.');
                navigate(`/collections/${data._id}`, { replace: true });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Save failed.');
        }
    }, [collectionId, isEdit, navigate]);

    /* ── Field drag reorder ─────────────────────────────── */
    const handleDragEnd = useCallback(async ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = fields.findIndex(f => f._id === active.id);
        const newIndex = fields.findIndex(f => f._id === over.id);
        const reordered = arrayMove(fields, oldIndex, newIndex).map((f, i) => ({ ...f, order: i }));
        setFields(reordered);
        try {
            await api.put(`/collectiondefs/${collectionId}/fields/reorder`,
                reordered.map(f => ({ _id: f._id, order: f.order }))
            );
        } catch {
            toast.error('Failed to save order.');
            fetchFields();
        }
    }, [fields, collectionId, fetchFields]);

    /* ── Field create / edit ────────────────────────────── */
    const handleFieldSubmit = useCallback(async (values) => {
        try {
            setFieldSaving(true);
            const payload = {
                ...values,
                minValue:  values.minValue  !== '' ? Number(values.minValue)  : undefined,
                maxValue:  values.maxValue  !== '' ? Number(values.maxValue)  : undefined,
                minLength: values.minLength !== '' ? Number(values.minLength) : undefined,
                maxLength: values.maxLength !== '' ? Number(values.maxLength) : undefined,
            };
            if (editField) {
                await api.put(`/collectiondefs/${collectionId}/fields/${editField._id}`, payload);
                toast.success('Field updated.');
            } else {
                await api.post(`/collectiondefs/${collectionId}/fields`, payload);
                toast.success('Field created.');
            }
            setDialogOpen(false);
            fetchFields();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Save failed.');
        } finally {
            setFieldSaving(false);
        }
    }, [editField, collectionId, fetchFields]);

    /* ── Field delete ───────────────────────────────────── */
    const handleFieldDelete = useCallback(async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/collectiondefs/${collectionId}/fields/${deleteTarget._id}`);
            setFields(prev => prev.filter(f => f._id !== deleteTarget._id));
            toast.success('Field deleted.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Delete failed.');
        } finally {
            setDeleteTarget(null);
        }
    }, [deleteTarget, collectionId]);

    const openCreate = () => { setEditField(null); setDialogOpen(true); };
    const openEdit   = (f) => { setEditField(f);    setDialogOpen(true); };

    const fieldDialogInitialValues = editField
        ? { ...FIELD_INITIAL, ...editField,
            minValue:  editField.minValue  ?? '',
            maxValue:  editField.maxValue  ?? '',
            minLength: editField.minLength ?? '',
            maxLength: editField.maxLength ?? '',
          }
        : FIELD_INITIAL;

    if (loading) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
        </FlexCenter>
    );


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Collection Definition"
                    subtitle={isEdit ? 'Edit' : 'New'}
                />
                <StyledButton onClick={() => navigate('/collections')}>
                    <ArrowBackIosNew />
                </StyledButton>
            </FlexBetween>

            <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>

                {/* ── Collection Form ─────────────────────── */}
                <Formik
                    enableReinitialize
                    initialValues={collectionValues}
                    validationSchema={collectionSchema}
                    onSubmit={handleCollectionSubmit}
                >
                    {({ 
                        values, 
                        errors, 
                        touched, 
                        handleChange, 
                        handleBlur, 
                        handleSubmit: formSubmit,
                    }) => (
                        <form onSubmit={formSubmit}>
                            <Box 
                                display="grid" 
                                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                gap={2} 
                            >
                                <TextField 
                                    label="Name (slug)" 
                                    name="name" 
                                    size="small" 
                                    variant="filled" 
                                    required
                                    value={values.name} 
                                    onChange={handleChange} 
                                    onBlur={handleBlur}
                                    error={!!touched.name && !!errors.name}
                                    helperText={(touched.name && errors.name) || 'Lowercase, no spaces. e.g. "airports"'}
                                    sx={{ gridColumn: 'span 6' }}
                                    disabled={isEdit}
                                />

                                <TextField 
                                    label="Label" 
                                    name="label" 
                                    size="small" 
                                    variant="filled" 
                                    required
                                    value={values.label} 
                                    onChange={handleChange} 
                                    onBlur={handleBlur}
                                    error={!!touched.label && !!errors.label}
                                    helperText={touched.label && errors.label}
                                    sx={{ gridColumn: 'span 6' }}
                                />

                                <Autocomplete
                                    options={ICON_OPTIONS}
                                    value={values.icon || null}
                                    onChange={(_, val) => handleChange({ target: { name: 'icon', value: val ?? '' } })}
                                    renderOption={(props, option) => {
                                        const { key, ...rest } = props;
                                        const IconComp = MuiIcons[option];
                                        return (
                                            <Box 
                                                key={key} 
                                                component="li" {...rest} 
                                                display="flex" 
                                                alignItems="center" 
                                                gap={1}
                                            >
                                                {IconComp && <IconComp fontSize="small" />}
                                                <Typography variant="body2">
                                                    {option}
                                                </Typography>
                                            </Box>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="Icon (MUI icon name)" 
                                            size="small" 
                                            variant="filled" 
                                        />
                                    )}
                                    sx={{ gridColumn: 'span 6' }}
                                />

                                <TextField 
                                    label="Description" 
                                    name="description" 
                                    size="small" 
                                    variant="filled" 
                                    multiline 
                                    minRows={1}
                                    value={values.description} 
                                    onChange={handleChange}
                                    sx={{ gridColumn: 'span 6' }}
                                />

                                <Box 
                                    sx={{ 
                                        gridColumn: 'span 12', 
                                        display: 'flex', 
                                        alignItems: 'center' 
                                    }}
                                >
                                    <FormControlLabel 
                                        label="Is Active"
                                        control={
                                            <Checkbox 
                                                name="isActive" 
                                                checked={values.isActive} 
                                                onChange={handleChange} 
                                            />
                                        }
                                    />
                                </Box>

                                <Button 
                                    fullWidth 
                                    type="submit" 
                                    variant="contained" 
                                    color="secondary"
                                    sx={{ gridColumn: 'span 12', height: 42 }}
                                    disabled={isEdit ? !canEdit : !canCreate}
                                >
                                    {isEdit ? 'Update' : 'Create'}
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>

                {/* ── Fields Section (edit mode only) ─────── */}
                {isEdit && (
                    <Box mt={2}>
                        <Divider sx={{ mb: 2 }} />
                        <FlexBetween mb={2}>
                            <Typography variant="h6">Fields</Typography>
                            {canEdit && (
                                <Tooltip title="Add field" arrow>
                                    <span>
                                        <StyledButton onClick={openCreate}>
                                            <Add />
                                        </StyledButton>
                                    </span>
                                </Tooltip>
                            )}
                        </FlexBetween>

                        {fields.length === 0 ? (
                            <Typography color="text.secondary">
                                No fields yet. Click + to add your first field.
                            </Typography>
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                modifiers={[restrictToVerticalAxis]}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext 
                                    items={fields.map(f => f._id)} 
                                    strategy={verticalListSortingStrategy}
                                >
                                    {fields.map(field => (
                                        <SortableFieldRow
                                            key={field._id}
                                            field={field}
                                            onEdit={openEdit}
                                            onDelete={setDeleteTarget}
                                            canEdit={canEdit}
                                            canDelete={canDelete}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        )}
                    </Box>
                )}
            </Box>

            {/* ── Field Dialog ─────────────────────────────── */}
            <GenericDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title={editField ? `Edit: ${editField.label}` : 'New Field'}
                loading={fieldSaving}
            >
                <FieldDefForm
                    initialValues={fieldDialogInitialValues}
                    onSubmit={handleFieldSubmit}
                />
            </GenericDialog>

            <DeleteConfirm
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleFieldDelete}
                deleteType="single"
            />
        </Box>
    );
};

export default CollectionDefPage;
